import { ChangeDetectionStrategy, Component, ElementRef, input, viewChild } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { SignalStringFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level date input for signal forms.
 *
 * Everything store- and validation-related sits in `SignalStringFieldBaseDirective`; this
 * class adds only the native picker bounds and the calendar icon. Wiring a field
 * needs no selector, no action and no per-field plumbing:
 *
 * ```html
 * <pace-core-date-field [formField]="signalForm.dateValue" ariaLabel="Start date" />
 * ```
 *
 * The value is the HTML5 `yyyy-MM-dd` string the native input works with (empty
 * when unset) rather than a `moment`, which keeps any form model built from these
 * fields directly serializable into an NgRx slice.
 *
 * The bounds need inputs of their own because the `FormUiControl` contract types
 * `min`/`max` as numbers, which does not fit a date string — unlike the length and
 * pattern constraints on `pace-core-text-field`, which arrive through the contract.
 *
 * The field is keyboard-first: the native input's `dd`/`mm`/`yyyy` segments take typed
 * digits, so nothing here may steal a click that was aimed at one of them. The picker
 * therefore has exactly two triggers — the calendar adornment for the mouse, and
 * `Alt`+`ArrowDown` or `F4` for the keyboard, since the browser's own picker indicator
 * is hidden in favour of that adornment.
 */
@Component({
    selector: 'pace-date-field',
    templateUrl: './date-field.component.html',
    styleUrls: ['./date-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent],
})
export class DateFieldComponent extends SignalStringFieldBaseDirective {
    /** HTML5 date (`yyyy-MM-dd`) the native picker is limited to; empty for no limit */
    public readonly minDate = input('');
    public readonly maxDate = input('');

    public readonly faCalendar = faCalendar;

    private readonly dateInput = viewChild.required<ElementRef<HTMLInputElement>>('dateInput');

    public openCalendar(): void {
        if (!this.editable()) {
            return;
        }

        // focus first so the caret is back in the field once the picker closes
        this.dateInput().nativeElement.focus();
        this.dateInput().nativeElement.showPicker();
    }

    /**
     * The conventional "open the dropdown" keys, which the field has to honour itself
     * because the native picker indicator is hidden. A bare `ArrowDown` is left to the
     * input, which uses it to decrement the focused segment.
     */
    public onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'F4' || (event.altKey && event.key === 'ArrowDown')) {
            event.preventDefault();
            this.openCalendar();
        }
    }
}
